/**
 * Protected Route Component
 *
 * Ensures routes require authentication before rendering.
 * Shows login modal for unauthenticated users while preserving page context.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { LOADING_MESSAGES } from '../config/loadingMessages';
import { OroLoader } from './OroLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { openLoginModal } = useAuthModal();
  const location = useLocation();

  // Loading message - pick once on mount (#748)
  const [loadingMessage] = useState(
    () => LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]
  );

  // Show login modal if not authenticated (once loading is complete).
  // Preserve pathname + search so links carrying tokens (#532 delete-confirm,
  // #1335 email-change-confirm) survive the redirect through login.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      openLoginModal(location.pathname + location.search);
    }
  }, [isLoading, isAuthenticated, openLoginModal, location.pathname, location.search]);

  // Loading indicator (#2675): branded OroLoader, inline + decorative so the
  // outer status region owns the announcement (#748 minimal style, #750 a11y).
  const loadingView = (
    <div
      className="min-h-screen bg-oro-bg flex items-center justify-center p-4"
      role="status"
      aria-live="polite"
    >
      <p className="text-sm text-text-muted inline-flex items-center">
        {loadingMessage}
        <OroLoader variant="inline" decorative className="ml-2" />
      </p>
    </div>
  );

  // Show loading while checking authentication.
  if (isLoading) {
    return loadingView;
  }

  // Show protected content only when authenticated.
  if (!isAuthenticated) {
    // Minimal placeholder while the login modal is triggered.
    return loadingView;
  }

  // Render protected content
  return <>{children}</>;
}
